import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/client';
import {
  MegaphoneIcon, EnvelopeIcon, DevicePhoneMobileIcon,
  ClockIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Director-only broadcast tool. Compose a message → pick recipients
// (preset + optional exclusions) → pick channels (email, SMS, or both)
// → send. History list below shows past broadcasts with delivery stats.

export default function MessagesPage() {
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState('all_staff');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [channels, setChannels] = useState({ email: true, sms: false });
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showRecipients, setShowRecipients] = useState(false);

  useEffect(() => {
    api.get('/broadcasts/presets').then(({ data }) => setPresets(data)).catch(() => {});
  }, []);

  const loadPreview = useCallback(() => {
    api.get(`/broadcasts/preview?preset=${preset}`)
      .then(({ data }) => { setPreview(data); setExcluded(new Set()); })
      .catch(() => toast.error('Could not load recipient preview.'));
  }, [preset]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const loadHistory = useCallback(() => {
    api.get('/broadcasts').then(({ data }) => setHistory(data)).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const includedCount = preview
    ? preview.staff.filter(s => !excluded.has(s.id)).length
    : 0;
  const reachableEmail = preview
    ? preview.staff.filter(s => !excluded.has(s.id) && s.has_email).length
    : 0;
  const reachableSMS = preview
    ? preview.staff.filter(s => !excluded.has(s.id) && s.has_sms).length
    : 0;

  const toggleExclude = (id) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const send = async () => {
    if (!body.trim()) return toast.error('Write a message first.');
    if (!channels.email && !channels.sms) return toast.error('Pick at least one channel.');
    const chans = [];
    if (channels.email) chans.push('email');
    if (channels.sms)   chans.push('sms');
    const channelLabel = chans.map(c => c.toUpperCase()).join(' + ');
    if (!window.confirm(`Send to ${includedCount} staff via ${channelLabel}?\n\nEmail reaches ${reachableEmail}, SMS reaches ${reachableSMS}.`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/broadcasts', {
        subject: subject || null,
        body,
        channels: chans,
        preset,
        exclude_ids: [...excluded],
      });
      toast.success(`Sent: ${data.email_sent} email${data.email_sent === 1 ? '' : 's'}, ${data.sms_sent} SMS`);
      setSubject(''); setBody('');
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="animate-slide-up max-w-4xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
          <MegaphoneIcon className="w-6 h-6" /> Messages
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Broadcast announcements to staff via email and/or text. One-way only — staff get the message; they don't reply through here.
        </p>
      </header>

      {/* Compose card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <h2 className="text-base font-semibold text-gray-900">New broadcast</h2>

        <div>
          <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Recipients</label>
          <div className="flex flex-wrap gap-1.5">
            {presets.map(p => (
              <button key={p.id} onClick={() => setPreset(p.id)}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg border ${
                  preset === p.id
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
          {preview && (
            <p className="text-xs text-gray-500 mt-2">
              <strong>{includedCount}</strong> recipient{includedCount === 1 ? '' : 's'} ·{' '}
              {reachableEmail} reachable via email · {reachableSMS} via SMS.
              {excluded.size > 0 && ` · ${excluded.size} excluded`}
              {' '}<button onClick={() => setShowRecipients(v => !v)}
                className="text-orange-600 hover:text-orange-700 font-medium underline">
                {showRecipients ? 'Hide' : 'Review list'}
              </button>
            </p>
          )}
          {showRecipients && preview && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-2 bg-gray-50/50">
              <ul className="space-y-0.5">
                {preview.staff.map(s => {
                  const isExcluded = excluded.has(s.id);
                  return (
                    <li key={s.id}
                      className={`flex items-center justify-between text-xs px-2 py-1 rounded ${isExcluded ? 'opacity-40' : ''}`}>
                      <label className="flex items-center gap-2 flex-1 cursor-pointer">
                        <input type="checkbox" checked={!isExcluded} onChange={() => toggleExclude(s.id)}
                          className="accent-orange-500" />
                        <span>{s.first_name} {s.last_name}</span>
                      </label>
                      <div className="flex items-center gap-1">
                        {s.has_email
                          ? <EnvelopeIcon className="w-3 h-3 text-gray-400" title="Email on file" />
                          : <EnvelopeIcon className="w-3 h-3 text-gray-200" title="No email" />}
                        {s.has_sms
                          ? <DevicePhoneMobileIcon className="w-3 h-3 text-gray-400" title="Phone on file" />
                          : <DevicePhoneMobileIcon className="w-3 h-3 text-gray-200" title="No phone" />}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div>
          <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Channels</label>
          <div className="flex gap-2">
            <button onClick={() => setChannels({ ...channels, email: !channels.email })}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border ${
                channels.email
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-500 border-gray-200'
              }`}>
              <EnvelopeIcon className="w-3.5 h-3.5" /> Email
            </button>
            <button onClick={() => setChannels({ ...channels, sms: !channels.sms })}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border ${
                channels.sms
                  ? 'bg-green-50 text-green-700 border-green-300'
                  : 'bg-white text-gray-500 border-gray-200'
              }`}>
              <DevicePhoneMobileIcon className="w-3.5 h-3.5" /> SMS
            </button>
          </div>
          {channels.sms && (
            <p className="text-[10px] text-amber-700 mt-1">
              Heads up: each SMS costs ~$0.0075 ({reachableSMS} × ${(0.0075 * reachableSMS).toFixed(2)} for this batch).
              Keep SMS short and reserve it for time-sensitive messages.
            </p>
          )}
        </div>

        {channels.email && (
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Subject (email only)</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Friday is a half day"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
        )}

        <div>
          <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Message</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="Type your message here…"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-y" />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-gray-400">{body.length} characters</span>
            {channels.sms && body.length > 160 && (
              <span className="text-[10px] text-amber-700">
                SMS will be split into {Math.ceil(body.length / 160)} segments
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={send} disabled={sending || includedCount === 0}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-5 py-2 rounded-xl disabled:opacity-50">
            <MegaphoneIcon className="w-4 h-4" />
            {sending ? 'Sending…' : `Send to ${includedCount}`}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Recent broadcasts</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing sent yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {history.map(m => (
              <li key={m.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {m.subject || '(no subject)'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{m.body}</p>
                    <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-1">
                      <span className="inline-flex items-center gap-0.5">
                        <ClockIcon className="w-3 h-3" />
                        {new Date(m.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      {m.sender_name && <span>by {m.sender_name}</span>}
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-gray-500 flex-shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <EnvelopeIcon className="w-3 h-3" />
                      <span className="text-green-700 font-medium">{m.email_sent}</span>
                      {m.email_failed > 0 && (
                        <span className="text-red-700">/ {m.email_failed} failed</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 justify-end mt-0.5">
                      <DevicePhoneMobileIcon className="w-3 h-3" />
                      <span className="text-green-700 font-medium">{m.sms_sent}</span>
                      {m.sms_failed > 0 && (
                        <span className="text-red-700">/ {m.sms_failed} failed</span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
